import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronsUpDown } from 'lucide-react';
import {
  ChevronDownIcon,
  LocationIcon,
  MapIcon,
  SearchIcon,
} from '@hero/web-icons';
import { X } from 'lucide-react';
import {
  useRegionAllCitiesApi,
  useRegionSearchApi,
  useTranslateRegions,
} from '@hero/api-hooks';
import type { RegionCity } from '@hero/api';
import type { RegionType } from '@hero/schema';
import { cn } from '@hero/web-lib';
import { Button } from '../../../ui/Button';
import { Input } from '../../../ui/Input/Input';
import { Popover, PopoverContent, PopoverTrigger } from '../../../ui/Popover';
import { RegionName } from '../../../app/RegionName/RegionName';
import { RegionSelectorDrawer } from './RegionSelectorDrawer';

export interface RegionSelectorProps {
  /** Selected region `_id`. */
  value?: string | null;
  /** Called with the picked region (or `null` when cleared). */
  onChange: (region: RegionCity | null) => void;
  /**
   * Which region types the selector should surface. The DB supports
   * `country | state | city | neighborhood`; pass `['city']` for a lane
   * picker, `['city', 'neighborhood']` for a finer-grained address picker,
   * etc. Defaults to `['city']` (the common pricing/routing case).
   *
   * The browse list (empty query) is still cities-only (via
   * `useRegionAllCitiesApi`); once the user types ≥2 characters the popover
   * queries `GET /region/search` (Atlas) which honors `types` fully — so
   * non-city types are findable by search even though they don't show in
   * the initial list.
   */
  types?: RegionType[];
  placeholder?: string;
  /** Header title for the fallback map drawer. */
  drawerTitle?: string;
  /** Header description for the fallback map drawer. */
  drawerDescription?: string;
  disabled?: boolean;
  className?: string;
  /** Optional id (used for label association in parent forms). */
  id?: string;
  /** Trigger density. `sm` is for table cells, `md` (default) for forms. */
  size?: 'sm' | 'md';
  /** Hide the secondary X clear button. Defaults to `false`. */
  hideClear?: boolean;
  /**
   * Show the "Pin from map" footer that escalates to the full drawer (Google
   * places + larger list). Defaults to `true`; pass `false` to lock callers
   * to the in-network list only.
   */
  enableMapPicker?: boolean;
  /** Whether to render the popover inside a Radix Portal. */
  disablePortal?: boolean;
}

const DEFAULT_TYPES: RegionType[] = ['city'];

/**
 * Combobox-style selector for an in-network region (city, neighborhood, …).
 *
 * - **Primary path** (popover): The empty-query state browses the platform's
 *   city list; typing ≥2 characters ALSO queries `GET /region/search`
 *   (Atlas — fuzzy, aliases, ar/en/ur) so regions beyond the browse list's
 *   cap are still findable. Results are merged and deduped by `_id`.
 * - **Escalation path** (drawer): A footer button opens
 *   `RegionSelectorDrawer` which adds Google Places suggestions for unusual
 *   / informal names. A Google place that doesn't map to a backend region
 *   can be added to the network on the spot (created as `needs_review`) —
 *   the pricing resolver requires a backend region id.
 *
 * The trigger mirrors `SelectAsync`'s visual language so the picker feels at
 * home next to `CustomerSelector` / `AgreementSelector`.
 */
export function RegionSelector({
  value,
  onChange,
  types = DEFAULT_TYPES,
  placeholder,
  drawerTitle,
  drawerDescription,
  disabled,
  className,
  id,
  size = 'md',
  hideClear = false,
  enableMapPicker = true,
  disablePortal = false,
}: RegionSelectorProps) {
  const { t } = useTranslation(['m-region', 'c-main']);
  const [isOpen, setIsOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  // Last region the user picked. The pick may come from Atlas search or the
  // map drawer — i.e. a region NOT in the cities browse list — so we keep it
  // locally to render the trigger label without an extra fetch.
  const [lastPicked, setLastPicked] = useState<RegionCity | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const { data: regions = [], isLoading } = useRegionAllCitiesApi();

  // Server-side Atlas search — fuzzy, aliases, ar/en/ur, honors `types`, and
  // is NOT limited by the browse list's cap. The hook self-disables under 2
  // characters.
  const { data: searchHits = [], isFetching: isSearching } = useRegionSearchApi({
    q: debouncedQuery,
    types,
    limit: 20,
  });

  // Batch the localized names for the entire option list — one TanStack
  // query for the whole popover, not one per option.
  const codes = useMemo(() => {
    const all = [
      ...regions.map(r => r.customCode),
      ...searchHits.map(h => h.customCode),
    ];
    return [...new Set(all.filter((c): c is string => !!c))];
  }, [regions, searchHits]);
  const { display: translateRegion } = useTranslateRegions(codes);

  const selected = useMemo(() => {
    if (value == null) return null;
    return (
      regions.find(r => String(r._id) === String(value)) ??
      (lastPicked && String(lastPicked._id) === String(value) ? lastPicked : null)
    );
  }, [regions, value, lastPicked]);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setDebouncedQuery('');
    }
  }, [isOpen]);

  const filteredRegions = useMemo(() => {
    const term = query.trim().toLowerCase();

    const clientFiltered = term
      ? regions.filter(r => {
          // Match against both the localized name AND the English snapshot so a
          // dispatcher typing "Riyadh" still finds the row even in Arabic mode.
          const localized = r.customCode ? translateRegion(r.customCode).toLowerCase() : '';
          const display = (r.displayName ?? '').toLowerCase();
          const code = (r.customCode ?? '').toLowerCase();
          return localized.includes(term) || display.includes(term) || code.includes(term);
        })
      : regions;

    if (!term) return clientFiltered;

    // Atlas hits lead (relevance-ranked, cover regions beyond the browse
    // list); client matches fill in instantly while the search is in flight.
    const seen = new Set(searchHits.map(h => String(h._id)));
    const fromSearch: RegionCity[] = searchHits.map(h => ({
      _id: h._id,
      customCode: h.customCode,
      displayName: h.displayName,
    }));
    return [...fromSearch, ...clientFiltered.filter(r => !seen.has(String(r._id)))];
  }, [regions, searchHits, query, translateRegion]);

  // Hoist the active selection so it's visible without scrolling — same
  // ergonomic trick `SelectAsync` uses.
  const orderedRegions = useMemo(() => {
    if (!selected) return filteredRegions;
    const rest = filteredRegions.filter(r => String(r._id) !== String(selected._id));
    const selectedMatches = filteredRegions.some(
      r => String(r._id) === String(selected._id),
    );
    return selectedMatches ? [selected, ...rest] : filteredRegions;
  }, [filteredRegions, selected]);

  const isSm = size === 'sm';

  const handlePick = (region: RegionCity) => {
    setLastPicked(region);
    onChange(region);
    setIsOpen(false);
  };

  const openMapPicker = () => {
    setIsOpen(false);
    setIsDrawerOpen(true);
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange(null);
  };

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <div className={cn('flex items-stretch gap-1', className)}>
          <PopoverTrigger asChild>
            <Button
              id={id}
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={isOpen}
              disabled={disabled}
              className={cn(
                'flex-1 justify-between gap-1.5 text-start font-normal',
                isSm ? 'h-8 px-2 text-xs' : 'h-10 px-3',
                !selected && 'text-muted-foreground',
              )}>
              <span className="flex items-center gap-1.5 min-w-0">
                <LocationIcon
                  className={cn(
                    'flex-shrink-0',
                    isSm ? 'size-3.5' : 'size-4',
                    selected ? 'text-primary' : 'text-gray-300',
                  )}
                />
                <span className="truncate">
                  {selected ? (
                    <RegionName
                      customCode={selected.customCode}
                      fallback={selected.displayName}
                    />
                  ) : (
                    placeholder ??
                    t('selector.placeholder', { defaultValue: 'Pick a region' })
                  )}
                </span>
              </span>
              {isSm ? (
                <ChevronDownIcon className="size-3 opacity-50 flex-shrink-0" />
              ) : (
                <ChevronsUpDown className="size-4 opacity-50 flex-shrink-0" />
              )}
            </Button>
          </PopoverTrigger>

          {selected && !disabled && !hideClear && (
            <Button
              type="button"
              variant="outline"
              className={cn(isSm ? 'h-8 px-1.5' : 'px-2')}
              onClick={handleClear}
              aria-label={t('selector.clear', { defaultValue: 'Clear selection' })}>
              <X className={cn(isSm ? 'size-3' : 'size-4')} />
            </Button>
          )}
        </div>

        <PopoverContent
          align="start"
          disablePortal={disablePortal}
          className="p-0 w-[--radix-popover-trigger-width] min-w-[280px] flex flex-col overflow-hidden">
          <div className="border-b px-3 py-2 bg-white">
            <div className="relative">
              <SearchIcon className="absolute start-2 top-1/2 -translate-y-1/2 size-3.5 text-gray-400" />
              <Input
                ref={inputRef}
                autoFocus
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={t('selector.search_placeholder', {
                  defaultValue: 'Search regions…',
                })}
                className="h-8 ps-7 pe-2 text-xs border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </div>
          </div>

          <div className="max-h-[280px] overflow-y-auto py-1">
            {(isLoading && regions.length === 0) ||
            (isSearching && orderedRegions.length === 0) ? (
              <p className="px-4 py-6 text-center text-xs text-gray-400">
                {t('c-main:async_operations.loading', { defaultValue: 'Loading…' })}
              </p>
            ) : orderedRegions.length === 0 ? (
              <div className="px-4 py-6 text-center">
                <p className="text-xs font-medium text-gray-700">
                  {t('selector.no_results_title', { defaultValue: 'No regions match' })}
                </p>
                {enableMapPicker && (
                  <button
                    type="button"
                    onClick={openMapPicker}
                    className="mt-1 text-[11px] font-medium text-primary hover:underline">
                    {t('selector.no_results_action', {
                      defaultValue: 'Pin from map instead →',
                    })}
                  </button>
                )}
              </div>
            ) : (
              orderedRegions.map(region => {
                const isSelected =
                  selected != null && String(selected._id) === String(region._id);
                return (
                  <button
                    key={String(region._id)}
                    type="button"
                    onClick={() => handlePick(region)}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-1.5 text-start text-xs transition-colors hover:bg-gray-50',
                      isSelected && 'bg-primary/5',
                    )}>
                    <div
                      className={cn(
                        'flex size-6 items-center justify-center rounded-full flex-shrink-0',
                        isSelected
                          ? 'bg-primary text-white'
                          : 'bg-primary/10 text-primary',
                      )}>
                      <LocationIcon className="size-3" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <RegionName
                        customCode={region.customCode}
                        fallback={region.displayName}
                        className={cn(
                          'block truncate',
                          isSelected ? 'font-semibold text-gray-900' : 'text-gray-800',
                        )}
                      />
                      {region.customCode && (
                        <div className="text-[10px] uppercase tracking-wide text-gray-400">
                          {region.customCode}
                        </div>
                      )}
                    </div>
                    {isSelected && (
                      <span className="text-[9px] font-semibold uppercase tracking-wider text-primary">
                        {t('c-main:selected', { defaultValue: 'Selected' })}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {enableMapPicker && (
            <button
              type="button"
              onClick={openMapPicker}
              className="flex w-full items-center gap-2 border-t bg-gray-50/60 px-3 py-2 text-start text-xs font-medium text-gray-700 transition-colors hover:bg-gray-100">
              <MapIcon className="size-3.5 text-gray-500" />
              <span>
                {t('selector.open_map_action', {
                  defaultValue: "Can't find it? Pin from map",
                })}
              </span>
            </button>
          )}
        </PopoverContent>
      </Popover>

      <RegionSelectorDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title={drawerTitle}
        description={drawerDescription}
        value={value}
        types={types}
        onPick={region => {
          setLastPicked(region);
          onChange(region);
          setIsDrawerOpen(false);
        }}
      />
    </>
  );
}
